/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useState, type NamedExoticComponent } from 'react';

import { useIntl } from 'react-intl';

import Badge from '@atlaskit/badge';
import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import TrashIcon from '@atlaskit/icon/core/delete';
import LinkIcon from '@atlaskit/icon/core/link';
import StarFilledIcon from '@atlaskit/icon/core/star-starred';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { PromptCard } from '../../controllers/prompt-cards';
import { generateShareLink } from '../rovo-conversation-actions/components/share-prompt-modal/utils/share-link';

import { DeleteConfirmModal } from './DeleteConfirmModal';
import { messages } from './messages';

const styles = cssMap({
	toolbar: {
		position: 'fixed',
		bottom: token('space.200'),
		left: '50%',
		transform: 'translateX(-50%)',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		paddingTop: token('space.100'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		zIndex: 500,
	},
	toolbarCount: {
		marginRight: token('space.100'),
		whiteSpace: 'nowrap',
	},
});

export type BulkFavoriteItem = {
	shareId: string;
	isFavorite: boolean;
};

export type PromptCardsMultiselectToolbarProps = {
	selectedCount: number;
	selectedCards: PromptCard[];
	onClearSelection: () => void;
	/** Bulk favorite handler - receives array of {shareId, isFavorite} items and systemPromptCount for analytics */
	onBulkFavorite?: (prompts: BulkFavoriteItem[], systemPromptCount: number) => Promise<void>;
	onDeletePrompt?: (promptId: string) => Promise<void>;
	/** Bulk delete handler - deletes multiple prompts and refetches the list. Preferred over onDeletePrompt for multi-select. */
	onBulkDelete?: (promptIds: string[], systemPromptCount: number) => Promise<void>;
	/** Bulk share handler - makes prompts public before copying link. Only called with user-generated prompt IDs (system prompts are already public). */
	onBulkShare?: (promptIds: string[], systemPromptCount: number) => Promise<void>;
};

export const PromptCardsMultiselectToolbar: NamedExoticComponent<PromptCardsMultiselectToolbarProps> =
	memo<PromptCardsMultiselectToolbarProps>(
		({
			selectedCount,
			selectedCards,
			onClearSelection,
			onBulkFavorite,
			onDeletePrompt,
			onBulkDelete,
			onBulkShare,
		}) => {
			const { formatMessage } = useIntl();
			const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
			const [isDeleting, setIsDeleting] = useState(false);
			const [isTogglingFavorites, setIsTogglingFavorites] = useState(false);
			const [isSharing, setIsSharing] = useState(false);
			const [showCopiedMessage, setShowCopiedMessage] = useState(false);

			const allFavorited = selectedCards.length > 0 && selectedCards.every((c) => c.isFavorited);
			const anyFavorited = selectedCards.some((c) => c.isFavorited);
			const systemPromptCount = selectedCards.filter((c) => !c.isUserGenerated).length;

			const handleMultiFavorite = async (isFavorite: boolean) => {
				if (!onBulkFavorite) return;
				setIsTogglingFavorites(true);
				try {
					const prompts: BulkFavoriteItem[] = selectedCards.map((c) => ({
						shareId: c.id,
						isFavorite,
					}));
					await onBulkFavorite(prompts, systemPromptCount);
					onClearSelection();
				} finally {
					setIsTogglingFavorites(false);
				}
			};

			const handleMultiDelete = async () => {
				if (!onBulkDelete && !onDeletePrompt) return;
				setIsDeleting(true);
				try {
					if (onBulkDelete) {
						// Preferred: bulk delete and refetch in one call
						await onBulkDelete(
							selectedCards.map((c) => c.id),
							systemPromptCount,
						);
					} else if (onDeletePrompt) {
						// Fallback: delete one-by-one (may trigger multiple refetches)
						await Promise.all(selectedCards.map((c) => onDeletePrompt(c.id)));
					}
					setIsDeleteConfirmOpen(false);
					onClearSelection();
				} finally {
					setIsDeleting(false);
				}
			};

			const handleMultiShare = async () => {
				setIsSharing(true);
				try {
					const allPromptIds = selectedCards.map((c) => c.id);
					// Only user-generated prompts need to be made public - system prompts are already public
					const userGeneratedPromptIds = selectedCards
						.filter((c) => c.isUserGenerated)
						.map((c) => c.id);
					// Make user-generated prompts public if handler is provided
					if (onBulkShare && userGeneratedPromptIds.length > 0) {
						await onBulkShare(userGeneratedPromptIds, systemPromptCount);
					}
					// Generate and copy the share link (includes all selected prompts)
					const shareUrl = generateShareLink(allPromptIds);
					await navigator.clipboard.writeText(shareUrl);
					setShowCopiedMessage(true);
					setTimeout(() => setShowCopiedMessage(false), 2000);
				} finally {
					setIsSharing(false);
				}
			};

			return (
				<Box>
					<Box xcss={styles.toolbar}>
						<Inline xcss={styles.toolbarCount} space="space.075" alignBlock="center">
							<Badge appearance="neutral">{selectedCount}</Badge>
							<Text size="medium" weight="medium" color="color.text.subtle">
								{formatMessage(messages.selectedLabel)}
							</Text>
						</Inline>
						{onBulkFavorite && !allFavorited && (
							<Button
								appearance="subtle"
								iconBefore={StarUnstarredIcon}
								onClick={() => handleMultiFavorite(true)}
								isDisabled={isTogglingFavorites}
							>
								{formatMessage(messages.multiAddToFavorites)}
							</Button>
						)}
						{onBulkFavorite && anyFavorited && (
							<Button
								appearance="subtle"
								iconBefore={StarFilledIcon}
								onClick={() => handleMultiFavorite(false)}
								isDisabled={isTogglingFavorites}
							>
								{formatMessage(messages.multiRemoveFromFavorites)}
							</Button>
						)}
						<Button
							appearance="subtle"
							iconBefore={LinkIcon}
							onClick={handleMultiShare}
							isDisabled={isSharing}
						>
							{showCopiedMessage
								? formatMessage(messages.linkCopied)
								: formatMessage(messages.multiShare)}
						</Button>
						{(onBulkDelete || onDeletePrompt) && (
							<Button
								appearance="subtle"
								iconBefore={TrashIcon}
								onClick={() => setIsDeleteConfirmOpen(true)}
								isDisabled={isDeleting}
							>
								{formatMessage(messages.multiDelete)}
							</Button>
						)}
						<IconButton
							icon={() => <CrossIcon label="" />}
							label={formatMessage(messages.clearSelection)}
							appearance="subtle"
							spacing="compact"
							onClick={onClearSelection}
						/>
					</Box>
					<DeleteConfirmModal
						title={formatMessage(messages.multiDeleteConfirmTitle, { count: selectedCount })}
						body={formatMessage(messages.multiDeleteConfirmBody, { count: selectedCount })}
						isOpen={isDeleteConfirmOpen}
						isLoading={isDeleting}
						onClose={() => setIsDeleteConfirmOpen(false)}
						onConfirm={handleMultiDelete}
						testId="multi-delete-prompt-confirm-modal"
					/>
				</Box>
			);
		},
	);
